/** Comparar uma nuvem de pontos com a geometria real de elementos IFC.
 *
 *  Substitui o "Desvio ao IFC" antigo, que media cada ponto à CAIXA do
 *  elemento mais próximo. Uma caixa não é o elemento: um ponto no vão
 *  entre os banzos de uma viga em I, fora do aço e dentro da caixa, dava
 *  zero. E media o levantamento inteiro contra o modelo inteiro, com o
 *  terreno e as árvores a contar como desvio.
 *
 *  Aqui cada ponto mede-se à superfície do elemento, triângulo a
 *  triângulo, e só aos elementos que se escolheram. Um ponto só conta
 *  para um elemento se estiver a menos de `raio` dele: o resto é outra
 *  coisa (terreno, vizinhos, mobiliário) e não entra nas contas.
 *
 *  Tudo aqui é puro e sem three.js, para se poder testar com números.
 *  As coordenadas são relativas a uma origem comum (a do
 *  `collectWorldTriangles`), senão um modelo georreferenciado perdia os
 *  milímetros no Float32.
 */

/** Um elemento a comparar: os seus triângulos, já na origem comum. */
export interface ElementoAComparar {
  /** Identificação livre do chamador (modelo e localId vivem fora daqui). */
  chave: string
  positions: Float32Array
  indices: Uint32Array
}

/** Distância ao quadrado de um ponto a um triângulo.
 *
 *  O ponto mais próximo por regiões de Voronoi (Ericson, "Real-Time
 *  Collision Detection", 5.1.5). Ao quadrado, porque a raiz só se tira
 *  uma vez, no fim, para o triângulo que ganhou. */
export function distancia2PontoTriangulo(
  px: number, py: number, pz: number,
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  cx: number, cy: number, cz: number,
  q?: number[],
): number {
  const abx = bx - ax, aby = by - ay, abz = bz - az
  const acx = cx - ax, acy = cy - ay, acz = cz - az
  const apx = px - ax, apy = py - ay, apz = pz - az
  const d1 = abx * apx + aby * apy + abz * apz
  const d2 = acx * apx + acy * apy + acz * apz
  let qx: number, qy: number, qz: number
  if (d1 <= 0 && d2 <= 0) {
    qx = ax; qy = ay; qz = az
  } else {
    const bpx = px - bx, bpy = py - by, bpz = pz - bz
    const d3 = abx * bpx + aby * bpy + abz * bpz
    const d4 = acx * bpx + acy * bpy + acz * bpz
    if (d3 >= 0 && d4 <= d3) {
      qx = bx; qy = by; qz = bz
    } else {
      const vc = d1 * d4 - d3 * d2
      if (vc <= 0 && d1 >= 0 && d3 <= 0) {
        const v = d1 / (d1 - d3)
        qx = ax + v * abx; qy = ay + v * aby; qz = az + v * abz
      } else {
        const cpx = px - cx, cpy = py - cy, cpz = pz - cz
        const d5 = abx * cpx + aby * cpy + abz * cpz
        const d6 = acx * cpx + acy * cpy + acz * cpz
        if (d6 >= 0 && d5 <= d6) {
          qx = cx; qy = cy; qz = cz
        } else {
          const vb = d5 * d2 - d1 * d6
          if (vb <= 0 && d2 >= 0 && d6 <= 0) {
            const w = d2 / (d2 - d6)
            qx = ax + w * acx; qy = ay + w * acy; qz = az + w * acz
          } else {
            const va = d3 * d6 - d5 * d4
            if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0) {
              const w = (d4 - d3) / (d4 - d3 + (d5 - d6))
              qx = bx + w * (cx - bx); qy = by + w * (cy - by); qz = bz + w * (cz - bz)
            } else {
              const den = 1 / (va + vb + vc)
              // Um triângulo degenerado (área zero) dá den infinito.
              // Acontece em IFC reais; cai para o vértice A em vez de
              // devolver NaN e estragar a média do elemento.
              if (!Number.isFinite(den)) {
                qx = ax; qy = ay; qz = az
              } else {
                const v = vb * den
                const w = vc * den
                qx = ax + abx * v + acx * w
                qy = ay + aby * v + acy * w
                qz = az + abz * v + acz * w
              }
            }
          }
        }
      }
    }
  }
  if (q) {
    q[0] = qx; q[1] = qy; q[2] = qz
  }
  const dx = px - qx, dy = py - qy, dz = pz - qz
  return dx * dx + dy * dy + dz * dz
}

/** A grelha que diz, para um ponto, que triângulos podem estar a menos
 *  de `raio` dele.
 *
 *  Cada triângulo entra em todas as células que a sua caixa, alargada
 *  pelo raio, toca. Assim um ponto só precisa de olhar para a SUA
 *  célula: se um triângulo está a menos de `raio`, está lá. */
export interface Grelha {
  celula: number
  raio: number
  min: [number, number, number]
  n: [number, number, number]
  /** célula -> lista plana de pares (elemento, triângulo). */
  celulas: Map<number, Int32Array>
  elementos: ElementoAComparar[]
  /** O plano de cada triângulo, por elemento: nx, ny, nz, d, com a
   *  normal unitária, indexado por triângulo (t / 3). */
  planos: Float64Array[]
  entradas: number
}

/** O plano de cada triângulo de um elemento.
 *
 *  Para o teste rápido: a distância ao plano nunca é maior do que a
 *  distância ao triângulo, por isso se o plano já está mais longe do
 *  que o melhor achado, o triângulo não pode ganhar e salta-se com
 *  quatro multiplicações. Os perfis de aço têm triângulos da altura
 *  toda do pilar, que entram em todas as células ao longo dele; sem
 *  isto cada ponto testava centenas de triângulos (medido: 20
 *  microssegundos por ponto, minutos para um levantamento). */
export function planosDosTriangulos(e: ElementoAComparar): Float64Array {
  const p = e.positions, ix = e.indices
  const out = new Float64Array((ix.length / 3) * 4)
  for (let t = 0, k = 0; t + 2 < ix.length; t += 3, k += 4) {
    const a = ix[t] * 3, b = ix[t + 1] * 3, c = ix[t + 2] * 3
    const ux = p[b] - p[a], uy = p[b + 1] - p[a + 1], uz = p[b + 2] - p[a + 2]
    const vx = p[c] - p[a], vy = p[c + 1] - p[a + 1], vz = p[c + 2] - p[a + 2]
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx
    const n = Math.hypot(nx, ny, nz)
    if (n < 1e-15) {
      // Degenerado: sem plano, nunca se salta.
      out[k + 3] = NaN
      continue
    }
    nx /= n; ny /= n; nz /= n
    out[k] = nx; out[k + 1] = ny; out[k + 2] = nz
    out[k + 3] = -(nx * p[a] + ny * p[a + 1] + nz * p[a + 2])
  }
  return out
}

/** Pode o triângulo t (índice nos indices) estar a menos de sqrt(melhor)? */
function podeGanhar(pl: Float64Array, t: number, x: number, y: number, z: number, melhor: number): boolean {
  const k = (t / 3) * 4
  const d = pl[k + 3]
  if (d !== d) return true
  const s = pl[k] * x + pl[k + 1] * y + pl[k + 2] * z + d
  return s * s <= melhor
}

/** Quantas entradas a grelha pode ter antes de se tentar células
 *  maiores. Uma laje de 30 x 30 m com células de 20 cm são dezenas de
 *  milhares de células por triângulo; com isto a grelha não come a
 *  memória toda. */
export const MAX_ENTRADAS = 12_000_000

export function construirGrelha(
  elementos: ElementoAComparar[],
  raio: number,
  celulaInicial?: number,
  maxEntradas = MAX_ENTRADAS,
): Grelha | null {
  const lo: [number, number, number] = [Infinity, Infinity, Infinity]
  const hi: [number, number, number] = [-Infinity, -Infinity, -Infinity]
  for (const e of elementos) {
    const p = e.positions
    for (let i = 0; i < p.length; i += 3) {
      if (p[i] < lo[0]) lo[0] = p[i]
      if (p[i + 1] < lo[1]) lo[1] = p[i + 1]
      if (p[i + 2] < lo[2]) lo[2] = p[i + 2]
      if (p[i] > hi[0]) hi[0] = p[i]
      if (p[i + 1] > hi[1]) hi[1] = p[i + 1]
      if (p[i + 2] > hi[2]) hi[2] = p[i + 2]
    }
  }
  if (!Number.isFinite(lo[0])) return null
  const min: [number, number, number] = [lo[0] - raio, lo[1] - raio, lo[2] - raio]
  const max: [number, number, number] = [hi[0] + raio, hi[1] + raio, hi[2] + raio]
  let celula = Math.max(celulaInicial ?? raio * 2, 0.05)
  for (let tentativa = 0; tentativa < 16; tentativa += 1) {
    const g = tentarGrelha(elementos, raio, celula, min, max, maxEntradas)
    if (g) return g
    celula *= 1.6
  }
  return null
}

function tentarGrelha(
  elementos: ElementoAComparar[],
  raio: number,
  celula: number,
  min: [number, number, number],
  max: [number, number, number],
  maxEntradas: number,
): Grelha | null {
  const n: [number, number, number] = [
    Math.max(1, Math.ceil((max[0] - min[0]) / celula)),
    Math.max(1, Math.ceil((max[1] - min[1]) / celula)),
    Math.max(1, Math.ceil((max[2] - min[2]) / celula)),
  ]
  const listas = new Map<number, number[]>()
  let entradas = 0
  for (let ei = 0; ei < elementos.length; ei += 1) {
    const { positions: p, indices: ix } = elementos[ei]
    for (let t = 0; t + 2 < ix.length; t += 3) {
      const a = ix[t] * 3, b = ix[t + 1] * 3, c = ix[t + 2] * 3
      const i0 = limitar(Math.floor((Math.min(p[a], p[b], p[c]) - raio - min[0]) / celula), n[0])
      const j0 = limitar(Math.floor((Math.min(p[a + 1], p[b + 1], p[c + 1]) - raio - min[1]) / celula), n[1])
      const k0 = limitar(Math.floor((Math.min(p[a + 2], p[b + 2], p[c + 2]) - raio - min[2]) / celula), n[2])
      const i1 = limitar(Math.floor((Math.max(p[a], p[b], p[c]) + raio - min[0]) / celula), n[0])
      const j1 = limitar(Math.floor((Math.max(p[a + 1], p[b + 1], p[c + 1]) + raio - min[1]) / celula), n[1])
      const k1 = limitar(Math.floor((Math.max(p[a + 2], p[b + 2], p[c + 2]) + raio - min[2]) / celula), n[2])
      entradas += (i1 - i0 + 1) * (j1 - j0 + 1) * (k1 - k0 + 1)
      if (entradas > maxEntradas) return null
      for (let k = k0; k <= k1; k += 1)
        for (let j = j0; j <= j1; j += 1)
          for (let i = i0; i <= i1; i += 1) {
            const chave = i + n[0] * (j + n[1] * k)
            let l = listas.get(chave)
            if (!l) listas.set(chave, (l = []))
            l.push(ei, t)
          }
    }
  }
  const celulas = new Map<number, Int32Array>()
  for (const [k, l] of listas) celulas.set(k, Int32Array.from(l))
  const planos = elementos.map(planosDosTriangulos)
  return { celula, raio, min, n, celulas, elementos, planos, entradas }
}

function limitar(v: number, n: number): number {
  return v < 0 ? 0 : v >= n ? n - 1 : v
}

/** O elemento mais próximo de um ponto, a menos de `raio`, e a que
 *  distância. `elemento` -1 quando não há nenhum. */
export interface MaisProximo {
  elemento: number
  distancia: number
}

export function maisProximo(
  g: Grelha,
  x: number, y: number, z: number,
  out: MaisProximo = { elemento: -1, distancia: Infinity },
): MaisProximo {
  out.elemento = -1
  out.distancia = Infinity
  const i = Math.floor((x - g.min[0]) / g.celula)
  const j = Math.floor((y - g.min[1]) / g.celula)
  const k = Math.floor((z - g.min[2]) / g.celula)
  if (i < 0 || j < 0 || k < 0 || i >= g.n[0] || j >= g.n[1] || k >= g.n[2]) return out
  const lista = g.celulas.get(i + g.n[0] * (j + g.n[1] * k))
  if (!lista) return out
  let melhor = g.raio * g.raio
  let quem = -1
  for (let q = 0; q < lista.length; q += 2) {
    const t = lista[q + 1]
    if (!podeGanhar(g.planos[lista[q]], t, x, y, z, melhor)) continue
    const e = g.elementos[lista[q]]
    const p = e.positions
    const a = e.indices[t] * 3, b = e.indices[t + 1] * 3, c = e.indices[t + 2] * 3
    const d2 = distancia2PontoTriangulo(
      x, y, z,
      p[a], p[a + 1], p[a + 2],
      p[b], p[b + 1], p[b + 2],
      p[c], p[c + 1], p[c + 2],
    )
    if (d2 <= melhor) {
      melhor = d2
      quem = lista[q]
    }
  }
  if (quem >= 0) {
    out.elemento = quem
    out.distancia = Math.sqrt(melhor)
  }
  return out
}

/** O ponto da superfície de UM elemento mais próximo de um ponto, a
 *  menos de `raio`. Devolve a distância, ou -1 se não houver, e escreve
 *  o ponto em `q`, e em `normal` a normal do triângulo. Serve para o eixo: saber para que lado o ponto
 *  medido fugiu do projecto, e não só quanto. */
export function pontoMaisProximoDoElemento(
  g: Grelha,
  elemento: number,
  x: number, y: number, z: number,
  q: number[],
  normal?: number[],
): number {
  const i = Math.floor((x - g.min[0]) / g.celula)
  const j = Math.floor((y - g.min[1]) / g.celula)
  const k = Math.floor((z - g.min[2]) / g.celula)
  if (i < 0 || j < 0 || k < 0 || i >= g.n[0] || j >= g.n[1] || k >= g.n[2]) return -1
  const lista = g.celulas.get(i + g.n[0] * (j + g.n[1] * k))
  if (!lista) return -1
  const e = g.elementos[elemento]
  const p = e.positions
  const t0 = [0, 0, 0]
  let melhor = g.raio * g.raio
  let achou = false
  const pl = g.planos[elemento]
  for (let s = 0; s < lista.length; s += 2) {
    if (lista[s] !== elemento) continue
    const t = lista[s + 1]
    if (!podeGanhar(pl, t, x, y, z, melhor)) continue
    const a = e.indices[t] * 3, b = e.indices[t + 1] * 3, c = e.indices[t + 2] * 3
    const d2 = distancia2PontoTriangulo(
      x, y, z,
      p[a], p[a + 1], p[a + 2],
      p[b], p[b + 1], p[b + 2],
      p[c], p[c + 1], p[c + 2],
      t0,
    )
    if (d2 <= melhor) {
      melhor = d2
      achou = true
      q[0] = t0[0]; q[1] = t0[1]; q[2] = t0[2]
      if (normal) {
        const ux = p[b] - p[a], uy = p[b + 1] - p[a + 1], uz = p[b + 2] - p[a + 2]
        const vx = p[c] - p[a], vy = p[c + 1] - p[a + 1], vz = p[c + 2] - p[a + 2]
        const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx
        const k = Math.hypot(nx, ny, nz) || 1
        normal[0] = nx / k; normal[1] = ny / k; normal[2] = nz / k
      }
    }
  }
  return achou ? Math.sqrt(melhor) : -1
}

/** Um plano de corte, em coordenadas já relativas à origem comum.
 *  Fica o lado em que nx*x + ny*y + nz*z + d >= 0, como no three.js. */
export interface PlanoRelativo {
  nx: number
  ny: number
  nz: number
  d: number
}

/** Passar um plano de mundo para a origem comum: o `d` absorve a
 *  translação, e as contas por ponto ficam com números pequenos. */
export function planoRelativo(
  normal: readonly [number, number, number],
  constante: number,
  origem: readonly [number, number, number],
): PlanoRelativo {
  return {
    nx: normal[0],
    ny: normal[1],
    nz: normal[2],
    d: constante + normal[0] * origem[0] + normal[1] * origem[1] + normal[2] * origem[2],
  }
}

export function dentroDosPlanos(planos: PlanoRelativo[], x: number, y: number, z: number): boolean {
  for (const p of planos) if (p.nx * x + p.ny * y + p.nz * z + p.d < 0) return false
  return true
}

/** O que se sabe de um elemento depois da comparação. */
export interface Estatistica {
  pontos: number
  soma: number
  somaQuadrados: number
  max: number
  dentro: number
  /** Uma amostra dos pontos que lhe calharam, para os percentis e para
   *  reconstruir o eixo sem guardar milhões. xyz na origem comum. */
  amostra: number[]
  distancias: number[]
}

/** Quantos pontos se guardam por elemento. Chega para um percentil e
 *  para ajustar um eixo; mais é memória sem ganho. */
export const AMOSTRA_MAX = 4000

export function novaEstatistica(): Estatistica {
  return { pontos: 0, soma: 0, somaQuadrados: 0, max: 0, dentro: 0, amostra: [], distancias: [] }
}

/** Juntar um ponto à estatística do seu elemento.
 *
 *  A amostra é um reservatório (Vitter, algoritmo R): cada ponto tem a
 *  mesma probabilidade de lá ficar, venha no princípio ou no fim do
 *  ficheiro. Guardar só os primeiros enviesava para a zona que o
 *  scanner varreu primeiro. `aleatorio` entra por fora para os testes
 *  serem repetíveis. */
export function acumular(
  s: Estatistica,
  x: number, y: number, z: number,
  distancia: number,
  tolerancia: number,
  aleatorio: () => number = Math.random,
): void {
  s.pontos += 1
  s.soma += distancia
  s.somaQuadrados += distancia * distancia
  if (distancia > s.max) s.max = distancia
  if (distancia <= tolerancia) s.dentro += 1
  if (s.distancias.length < AMOSTRA_MAX) {
    s.amostra.push(x, y, z)
    s.distancias.push(distancia)
  } else {
    const r = Math.floor(aleatorio() * s.pontos)
    if (r < AMOSTRA_MAX) {
      s.amostra[r * 3] = x
      s.amostra[r * 3 + 1] = y
      s.amostra[r * 3 + 2] = z
      s.distancias[r] = distancia
    }
  }
}

/** Abaixo disto não se tira conclusão nenhuma sobre um elemento. É o
 *  pilar escondido atrás de uma parede, que o laser não viu: dizer que
 *  está bem, ou mal, com três pontos, era inventar. */
export const MIN_PONTOS = 30

export type Veredicto = 'conforme' | 'desvio' | 'nao-visto'

export interface LinhaDoRelatorio {
  indice: number
  chave: string
  pontos: number
  media: number
  rms: number
  p95: number
  max: number
  /** Fração dos pontos dentro da tolerância, de 0 a 1. */
  dentro: number
  veredicto: Veredicto
}

export function percentil(valores: number[], p: number): number {
  if (valores.length === 0) return 0
  const v = [...valores].sort((a, b) => a - b)
  const i = Math.min(v.length - 1, Math.max(0, Math.ceil(p * v.length) - 1))
  return v[i]
}

/** Uma linha por elemento, os piores primeiro e os não vistos no fim.
 *
 *  O veredicto olha para o percentil 95 e não para o máximo: num
 *  levantamento há sempre pontos soltos (um reflexo, uma pessoa que
 *  passou), e o máximo condenava todos os elementos. Está fora quando
 *  mais de 5% dos pontos passam da tolerância. */
export function relatorio(
  elementos: ElementoAComparar[],
  estatisticas: Estatistica[],
  tolerancia: number,
  minPontos = MIN_PONTOS,
): LinhaDoRelatorio[] {
  const linhas = elementos.map((e, indice): LinhaDoRelatorio => {
    const s = estatisticas[indice]
    const n = s.pontos
    const p95 = percentil(s.distancias, 0.95)
    return {
      indice,
      chave: e.chave,
      pontos: n,
      media: n ? s.soma / n : 0,
      rms: n ? Math.sqrt(s.somaQuadrados / n) : 0,
      p95,
      max: s.max,
      dentro: n ? s.dentro / n : 0,
      veredicto: n < minPontos ? 'nao-visto' : p95 <= tolerancia ? 'conforme' : 'desvio',
    }
  })
  const ordem: Record<Veredicto, number> = { desvio: 0, conforme: 1, 'nao-visto': 2 }
  return linhas.sort(
    (a, b) =>
      ordem[a.veredicto] - ordem[b.veredicto] ||
      b.p95 - a.p95 ||
      b.pontos - a.pontos,
  )
}

/** A cor de um ponto no modo de desvio, em 0..255.
 *  Verde dentro da tolerância, amarelo até ao triplo, vermelho depois.
 *  O que não pertence a nenhum elemento escolhido (distância negativa)
 *  fica cinzento escuro: continua a ver-se onde se está, mas não
 *  distrai. */
export function corDoDesvio(
  distancia: number,
  tolerancia: number,
  out: Uint8Array | number[],
  o: number,
): void {
  if (!(distancia >= 0) || !Number.isFinite(distancia)) {
    out[o] = 70; out[o + 1] = 70; out[o + 2] = 76
  } else if (distancia <= tolerancia) {
    out[o] = 46; out[o + 1] = 204; out[o + 2] = 90
  } else if (distancia <= tolerancia * 3) {
    out[o] = 255; out[o + 1] = 191; out[o + 2] = 26
  } else {
    out[o] = 242; out[o + 1] = 51; out[o + 2] = 51
  }
}
